import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NAVY = "#1E2761";
const ICE = "#CADCFC";
const GOLD = "#C9A227";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, requiresReview: false, error: null });

  useEffect(() => {
    if (!sessionId) { setState({ loading: false, paid: false, error: "Missing session" }); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setState({ loading: false, paid: true, requiresReview: !!res.data.requires_review });
          return;
        }
        attempts += 1;
        if (attempts < 15) setTimeout(poll, 2000);
        else setState({ loading: false, paid: false, error: "Payment not confirmed yet. Please contact us if this persists." });
      } catch {
        if (cancelled) return;
        setState({ loading: false, paid: false, error: "Could not verify payment" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center" style={{ backgroundColor: ICE }} data-testid="payment-success">
      <div className="max-w-lg text-center px-6 py-16 bg-white border" style={{ borderColor: `${NAVY}22` }}>
        {state.loading && (
          <>
            <Loader2 className="h-10 w-10 mx-auto mb-6 animate-spin" style={{ color: NAVY }} />
            <h1 className="font-serif text-2xl mb-2" style={{ color: NAVY }}>Confirming your payment...</h1>
            <p className="text-sm text-gray-600">Please don't close this window.</p>
          </>
        )}
        {!state.loading && state.paid && (
          <>
            <div className="h-16 w-16 flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: GOLD }}>
              <Check className="h-8 w-8 text-white" strokeWidth={2} />
            </div>
            <h1 className="font-serif text-3xl mb-3" style={{ color: NAVY }}>Payment Confirmed</h1>
            {state.requiresReview ? (
              <p className="text-gray-700 mb-8">
                Your application has been received. Our selection team will review your submission and contact you by email with the outcome.
              </p>
            ) : (
              <p className="text-gray-700 mb-8">
                Your Self-Funded seat is <strong>confirmed</strong>. A receipt has been sent to your email. Welcome to Barcelona 2027.
              </p>
            )}
            <Link to="/" className="inline-block px-8 py-3 text-xs tracking-[0.2em] uppercase text-white" style={{ backgroundColor: NAVY }} data-testid="home-btn">
              Return Home
            </Link>
          </>
        )}
        {!state.loading && !state.paid && (
          <>
            <AlertCircle className="h-12 w-12 mx-auto mb-6 text-red-600" strokeWidth={1.5} />
            <h1 className="font-serif text-2xl mb-3" style={{ color: NAVY }}>Payment Not Confirmed</h1>
            <p className="text-gray-700 mb-8">{state.error || "We couldn't confirm your payment."}</p>
            <Link to="/register" className="inline-block px-8 py-3 text-xs tracking-[0.2em] uppercase text-white" style={{ backgroundColor: NAVY }}>
              Try Again
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
